import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addEditExternalContactMedium,
  getCities,
  getCountries,
  getDistricts,
  getNeighborhoods,
  getStreets,
} from '../api';
import { useAuth } from '../hooks/useAuth';
import { IExternalContactMedium } from '../utils/types';

interface AddInvoiceAddressDialogProps {
  open: boolean;
  onClose: () => void;
  program?: IExternalContactMedium;
  onSave: (program: IExternalContactMedium) => void;
  salesIndividualId: string;
}

const AddInvoiceAddressDialog: React.FC<AddInvoiceAddressDialogProps> = ({
  open,
  onClose,
  program,
  onSave,
  salesIndividualId,
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString(); // Formats the date as YYYY-MM-DD
  };
  const getDatePlus50Years = () => {
    const futureDate = new Date();
    futureDate.setFullYear(futureDate.getFullYear() + 50);
    return futureDate.toISOString();
  };

  const [programData, setProgramData] = useState<IExternalContactMedium>({
    description: '',
    electronicAddressInput: {
      electronicAddress: '',
      typeId: 0,
    },
    fromDate: '',
    owner: '',
    phoneNumberInput: {
      countryCode: '',
      phoneNumber: '',
      typeId: 0,
    },
    postalAddressInput: {
      doorNumber: '',
      geographicBoundaryId: 0,
      postCode: '',
      streetAddress1: '',
      typeId: 0,
    },
    toDate: '',
  });

  const [countries, setCountries] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [cities, setCities] = useState<Array<{ id: number; name: string }>>([]);
  const [districts, setDistricts] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [neighborhoods, setNeighborhoods] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [streets, setStreets] = useState<Array<{ id: number; name: string }>>(
    [],
  );

  const [selectedCountry, setSelectedCountry] = useState<number>(18884);
  const [selectedCity, setSelectedCity] = useState<number | ''>('');
  const [selectedDistrict, setSelectedDistrict] = useState<number | ''>('');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<number | ''>(
    '',
  );
  const [selectedStreet, setSelectedStreet] = useState<number | ''>('');

  useEffect(() => {
    if (program && open) {
      setProgramData(program);
    } else if (!program && open) {
      setProgramData({
        description: '',
        electronicAddressInput: {
          electronicAddress: '',
          typeId: 6,
        },
        fromDate: getCurrentDate(),
        owner: '',
        phoneNumberInput: {
          countryCode: '90',
          phoneNumber: '',
          typeId: 2,
        },
        postalAddressInput: {
          doorNumber: '',
          geographicBoundaryId: 0,
          postCode: '',
          streetAddress1: '',
          typeId: 8,
        },
        toDate: getDatePlus50Years(),
      });
      setSelectedCountry(18884);
      setSelectedCity('');
      setSelectedDistrict('');
      setSelectedNeighborhood('');
      setSelectedStreet('');
      setCities([]);
      setDistricts([]);
      setNeighborhoods([]);
      setStreets([]);
    }
  }, [program, open]);

  useEffect(() => {
    const fetchCountriesData = async () => {
      try {
        const data = await getCountries(token);
        setCountries(data);
        setSelectedCountry(18884);
      } catch (error: any) {
        console.error(error);
        setError(error.message);
      }
    };

    fetchCountriesData();
  }, [token]);

  useEffect(() => {
    const fetchCitiesData = async (countryId: number) => {
      try {
        const data = await getCities(countryId, token);
        setCities(data);
      } catch (error: any) {
        setCities([]);
        console.error(error);
        setError(error.message);
      }
    };

    if (program && open) {
      setProgramData(program);
    } else if (!program && open) {
      setProgramData({
        description: '',
        electronicAddressInput: {
          electronicAddress: '',
          typeId: 6,
        },
        fromDate: getCurrentDate(),
        owner: '',
        phoneNumberInput: {
          countryCode: '90',
          phoneNumber: '',
          typeId: 2,
        },
        postalAddressInput: {
          doorNumber: '',
          geographicBoundaryId: 0,
          postCode: '',
          streetAddress1: '',
          typeId: 8,
        },
        toDate: getDatePlus50Years(),
      });
      setSelectedCountry(18884);
      setSelectedCity('');
      setSelectedDistrict('');
      setSelectedNeighborhood('');
      setSelectedStreet('');
      setCities([]);
      setDistricts([]);
      setNeighborhoods([]);
      setStreets([]);

      fetchCitiesData(18884);
    }
  }, [program, open]);

  useEffect(() => {
    if (selectedCity) {
      setSelectedDistrict('');
      setSelectedNeighborhood('');
      setSelectedStreet('');
      setDistricts([]);
      setNeighborhoods([]);
      setStreets([]);
      const fetchDistrictsData = async () => {
        try {
          const data = await getDistricts(selectedCity, token);
          setDistricts(data);
        } catch (error: any) {
          console.error(error);
          setError(error.message);
        }
      };

      fetchDistrictsData();
    }
  }, [selectedCity, token]);

  useEffect(() => {
    if (selectedDistrict) {
      setSelectedNeighborhood('');
      setSelectedStreet('');
      setNeighborhoods([]);
      setStreets([]);
      const fetchNeighborhoodsData = async () => {
        try {
          const data = await getNeighborhoods(selectedDistrict, token);
          setNeighborhoods(data);
        } catch (error: any) {
          console.error(error);
          setError(error.message);
        }
      };

      fetchNeighborhoodsData();
    }
  }, [selectedDistrict, token]);

  useEffect(() => {
    if (selectedNeighborhood) {
      setSelectedStreet('');
      setStreets([]);
      const fetchStreetsData = async () => {
        try {
          const data = await getStreets(selectedNeighborhood, token);
          setStreets(data);
        } catch (error: any) {
          console.error(error);
          setError(error.message);
        }
      };

      fetchStreetsData();

      setProgramData((prevState) => ({
        ...prevState,
        postalAddressInput: {
          ...prevState.postalAddressInput,
          geographicBoundaryId: selectedNeighborhood,
        },
      }));
    }
  }, [selectedNeighborhood, token]);

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { name, value } = event.target;

    setProgramData((prevState) => {
      const nameParts = name.split('.');

      const setNestedValue = (obj: any, parts: string[]): any => {
        if (parts.length === 1) {
          return { ...obj, [parts[0]]: value };
        }
        return {
          ...obj,
          [parts[0]]: setNestedValue(obj[parts[0]], parts.slice(1)),
        };
      };

      return setNestedValue(prevState, nameParts);
    });
  };

  const isSaveDisabled =
    !programData.phoneNumberInput.phoneNumber ||
    !programData.electronicAddressInput.electronicAddress ||
    !programData.owner ||
    !programData.phoneNumberInput.countryCode ||
    !programData.phoneNumberInput.phoneNumber ||
    !programData.postalAddressInput.doorNumber ||
    !programData.postalAddressInput.geographicBoundaryId ||
    !programData.postalAddressInput.streetAddress1;

  const fetchEditExternalContactMedium = async () => {
    try {
      setLoading(true);
      const res = await addEditExternalContactMedium(
        salesIndividualId,
        token,
        programData.description,
        programData.electronicAddressInput.electronicAddress,
        programData.electronicAddressInput.typeId,
        programData.fromDate,
        programData.owner,
        programData.phoneNumberInput.countryCode,
        programData.phoneNumberInput.phoneNumber,
        programData.phoneNumberInput.typeId,
        programData.postalAddressInput.doorNumber,
        programData.postalAddressInput.geographicBoundaryId,
        programData.postalAddressInput.postCode,
        programData.postalAddressInput.streetAddress1,
        programData.postalAddressInput.typeId,
        programData.toDate,
      );

      if (res?.status !== 200 && res?.status !== 201) {
        const data = await res.json();
        throw new Error(data.detail || 'Add/Edit Loyalty Program Error');
      }
    } catch (e: any) {
      setError(e.message);
      console.error('Add/Edit Loyalty Program Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await fetchEditExternalContactMedium();
    onSave(programData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t(
          program?.electronicAddressInput.electronicAddress
            ? 'orderDetailPage.addInvoiceAddressDialog.editPostalAddress'
            : 'orderDetailPage.addInvoiceAddressDialog.addPostalAddress',
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t(
                'orderDetailPage.addInvoiceAddressDialog.deliveryCustomerName',
              )}
              name="owner"
              value={programData.owner}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                flexBasis: '48%',
                flexGrow: 1,
              }}
            >
              <InputLabel id="country-label">
                {t('orderDetailPage.addInvoiceAddressDialog.country')}
              </InputLabel>
              <Select
                labelId="country-label"
                label={t('orderDetailPage.addInvoiceAddressDialog.country')}
                value={selectedCountry}
                disabled
                onChange={(e) => setSelectedCountry(e.target.value as number)}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCountry && (
              <FormControl
                fullWidth
                size="small"
                sx={{
                  flexBasis: selectedCountry ? '48%' : '100%',
                  flexGrow: 1,
                }}
              >
                <Autocomplete
                  options={cities}
                  getOptionLabel={(option) => option.name}
                  value={
                    cities.find((city) => city.id === selectedCity) || undefined
                  }
                  onChange={(event, newValue) => {
                    setSelectedCity(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('orderDetailPage.addInvoiceAddressDialog.city')}
                      variant="outlined"
                      size="small"
                      required
                    />
                  )}
                  disableClearable
                  loading={loading && cities.length === 0}
                />
              </FormControl>
            )}
            {selectedCity && (
              <FormControl
                fullWidth
                size="small"
                sx={{
                  flexBasis: selectedCity ? '48%' : '100%',
                  flexGrow: 1,
                }}
              >
                <Autocomplete
                  options={districts}
                  getOptionLabel={(option) => option.name}
                  value={
                    districts.find(
                      (district) => district.id === selectedDistrict,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setSelectedDistrict(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t(
                        'orderDetailPage.addInvoiceAddressDialog.district',
                      )}
                      variant="outlined"
                      size="small"
                      required
                    />
                  )}
                  loading={loading && districts.length === 0}
                />
              </FormControl>
            )}

            {selectedDistrict && (
              <FormControl
                fullWidth
                size="small"
                sx={{
                  flexBasis: selectedDistrict ? '48%' : '100%',
                  flexGrow: 1,
                }}
              >
                <Autocomplete
                  options={neighborhoods}
                  getOptionLabel={(option) => option.name}
                  value={
                    neighborhoods.find(
                      (neighborhood) =>
                        neighborhood.id === selectedNeighborhood,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setSelectedNeighborhood(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t(
                        'orderDetailPage.addInvoiceAddressDialog.neighborhood',
                      )}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  loading={loading && neighborhoods.length === 0}
                />
              </FormControl>
            )}

            {selectedNeighborhood && (
              <FormControl
                fullWidth
                size="small"
                sx={{
                  flexBasis: selectedNeighborhood ? '48%' : '100%',
                  flexGrow: 1,
                }}
              >
                <Autocomplete
                  options={streets}
                  getOptionLabel={(option) => option.name}
                  value={
                    streets.find((street) => street.id === selectedStreet) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    setSelectedStreet(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t(
                        'orderDetailPage.addInvoiceAddressDialog.street',
                      )}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  loading={loading && streets.length === 0}
                />
              </FormControl>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('orderDetailPage.addInvoiceAddressDialog.doorNumber')}
              name="postalAddressInput.doorNumber"
              value={programData.postalAddressInput.doorNumber}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />
            <TextField
              label={t('orderDetailPage.addInvoiceAddressDialog.postalCode')}
              name="postalAddressInput.postCode"
              value={programData.postalAddressInput.postCode}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('orderDetailPage.addInvoiceAddressDialog.streetAddress')}
              name="postalAddressInput.streetAddress1"
              value={programData.postalAddressInput.streetAddress1}
              onChange={handleChange}
              fullWidth
              size="small"
              required
            />
            <TextField
              label={t('orderDetailPage.addInvoiceAddressDialog.description')}
              name="description"
              value={programData.description}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogTitle>
        {t(
          program?.electronicAddressInput.electronicAddress
            ? 'orderDetailPage.addInvoiceAddressDialog.editPhoneNumber'
            : 'orderDetailPage.addInvoiceAddressDialog.addPhoneNumber',
        )}
      </DialogTitle>
      <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
        <TextField
          label={t('orderDetailPage.addInvoiceAddressDialog.countryCode')}
          name="phoneNumberInput.countryCode"
          value={'TR(90)'}
          disabled
          onChange={handleChange}
          fullWidth
          size="small"
        />
        <TextField
          label={t('orderDetailPage.addInvoiceAddressDialog.number')}
          name="phoneNumberInput.phoneNumber"
          value={programData.phoneNumberInput.phoneNumber}
          onChange={handleChange}
          fullWidth
          size="small"
          required
        />
      </Box>

      <DialogTitle>
        {t(
          program?.electronicAddressInput.typeId
            ? 'orderDetailPage.addInvoiceAddressDialog.editElectronicAddress'
            : 'orderDetailPage.addInvoiceAddressDialog.addElectronicAddress',
        )}
      </DialogTitle>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <TextField
          label={t(
            'orderDetailPage.addInvoiceAddressDialog.electronicAddressType',
          )}
          name="electronicAddressInput.typeId"
          value={t('orderDetailPage.addInvoiceAddressDialog.emailAddress')}
          onChange={handleChange}
          fullWidth
          disabled
          required
          size="small"
        />
        <TextField
          label={t(
            'orderDetailPage.addInvoiceAddressDialog.electronicAddresses',
          )}
          name="electronicAddressInput.electronicAddress"
          value={programData.electronicAddressInput.electronicAddress}
          onChange={handleChange}
          fullWidth
          required
          size="small"
        />
      </Box>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t('accountInfosPage.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={isSaveDisabled || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('accountInfosPage.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddInvoiceAddressDialog;
